import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as OkIcon,
  Cancel as FailIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';
import { parseServerTs } from '../../timezone';

const sourceColor = {
  operator: 'primary',
  deployment: 'default',
  backup: 'info',
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const ms = parseServerTs(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString();
};

const relativeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - parseServerTs(iso);
  if (Number.isNaN(ms)) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const detailsToChips = (details) => {
  if (details == null) return [];
  let obj = details;
  if (typeof obj === 'string') {
    try { obj = JSON.parse(obj); } catch { return [[null, details]]; }
  }
  if (typeof obj !== 'object') return [[null, String(obj)]];
  return Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '');
};

/**
 * Unified, read-only operator audit log. Pulls the merged stream from
 * {@code GET /api/infrastructure/audit} which blends OperatorAuditEvent +
 * DeploymentLog + ClickHouseBackupJob rows into one chronological feed.
 */
const ClickHouseAuditLogDialog = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ source: 'all', actor: '', action: '', windowDays: 7 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.source && filters.source !== 'all') params.source = filters.source;
      const since = new Date();
      since.setDate(since.getDate() - Number(filters.windowDays || 7));
      params.since = since.toISOString().slice(0, 19);
      params.limit = 500;
      const res = await billingService.auditLog(params);
      setRows(Array.isArray(res.data?.events) ? res.data.events : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Audit log load failed');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.source, filters.windowDays]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const visible = useMemo(() => {
    const actor = filters.actor.trim().toLowerCase();
    const action = filters.action.trim().toLowerCase();
    return rows.filter(r => {
      if (actor && !(r.actor || '').toLowerCase().includes(actor)) return false;
      if (action && !(r.action || '').toLowerCase().includes(action)) return false;
      return true;
    });
  }, [rows, filters.actor, filters.action]);

  const counts = useMemo(() => {
    const c = { operator: 0, deployment: 0, backup: 0, failures: 0 };
    rows.forEach(r => {
      c[r.source] = (c[r.source] || 0) + 1;
      if (!r.success) c.failures += 1;
    });
    return c;
  }, [rows]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Operator audit log
          <HelpIcon docKey="audit_log" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={load} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Window</Typography>
            <Select size="small" value={filters.windowDays}
                    onChange={(e) => setFilters(f => ({ ...f, windowDays: e.target.value }))}
                    sx={{ display: 'block', minWidth: 120 }}>
              <MenuItem value={1}>last 24 hours</MenuItem>
              <MenuItem value={7}>last 7 days</MenuItem>
              <MenuItem value={30}>last 30 days</MenuItem>
              <MenuItem value={90}>last 90 days</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Source</Typography>
            <Stack direction="row" spacing={0.5}>
              {['all', 'operator', 'deployment', 'backup'].map(s => (
                <Chip key={s} label={`${s}${s !== 'all' ? ` (${counts[s] || 0})` : ''}`}
                      variant={filters.source === s ? 'filled' : 'outlined'}
                      color={s === 'all' ? 'default' : sourceColor[s]}
                      size="small"
                      onClick={() => setFilters(f => ({ ...f, source: s }))} />
              ))}
            </Stack>
          </Box>
          <TextField size="small" label="Actor contains"
                     value={filters.actor}
                     onChange={(e) => setFilters(f => ({ ...f, actor: e.target.value }))}
                     sx={{ minWidth: 160 }} />
          <TextField size="small" label="Action contains"
                     value={filters.action}
                     onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))}
                     sx={{ minWidth: 220 }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Failures in the current window">
            <Chip color={counts.failures > 0 ? 'error' : 'default'}
                  label={`${counts.failures} failure${counts.failures === 1 ? '' : 's'}`}
                  size="small" />
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {visible.length} of {rows.length}
          </Typography>
        </Stack>

        {!loading && visible.length === 0 && (
          <Alert severity="info">No events in this window. Adjust filters or extend the window.</Alert>
        )}
        {visible.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 160 }}>When</TableCell>
                <TableCell sx={{ width: 100 }}>Source</TableCell>
                <TableCell sx={{ width: 120 }}>Actor</TableCell>
                <TableCell sx={{ width: 180 }}>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell sx={{ width: 40 }}>OK</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(r => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Tooltip title={fmtTime(r.timestamp)}>
                      <Typography variant="caption">{relativeAgo(r.timestamp)}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.source} size="small" color={sourceColor[r.source] || 'default'}
                          variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                      {r.actor}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{r.action}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption">{r.targetName || '—'}</Typography>
                      {r.targetId != null && (
                        <Typography variant="caption" color="text.disabled"
                                    sx={{ display: 'block', fontSize: '0.65rem' }}>
                          {r.targetType} #{r.targetId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {r.success
                      ? <OkIcon color="success" sx={{ fontSize: 18 }} />
                      : <Tooltip title={r.errorMessage || 'failed'}>
                          <FailIcon color="error" sx={{ fontSize: 18 }} />
                        </Tooltip>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {detailsToChips(r.details).map(([k, v], i) => (
                        <Chip key={i} size="small" variant="outlined"
                              label={k == null ? String(v) : `${k}: ${String(v).slice(0, 60)}`}
                              sx={{ fontSize: 10 }} />
                      ))}
                      {r.errorMessage && (
                        <Tooltip title={r.errorMessage}>
                          <Chip size="small" color="error" variant="outlined"
                                label={`err: ${r.errorMessage.slice(0, 60)}`}
                                sx={{ fontSize: 10 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClickHouseAuditLogDialog;
